import React from 'react';
import { Box, Typography, CircularProgress, Chip } from '@mui/material';
import { FaArrowLeft } from 'react-icons/fa';
import { COLORS } from '../../../../../../constants/radar';
import ReviewerCompetencyList from './ReviewerCompetencyList';
import ConfirmationModal from '../../../../../common/ConfirmationModal';

const SummaryStep = ({
  activeStep,
  assignment,
  myReviewer,
  competencies,
  employeeId,
  visualizationType,
  currentSkillTemplate,
  loadingSkillTemplate,
  skillLevels,
  loadingSegmentMap,
  loadingOverallSkill,
  isAllPreviousStepsComplete,
  isAssessmentSubmitted,
  // ADR-014 §7: parent session closed (snapshot close) => frozen, read-only
  sessionLocked = false,
  isDirty,
  submitting,
  confirmDialogOpen,
  setConfirmDialogOpen,
  handleStep,
  handleSubmitWithConfirmation,
  getCustomDescriptionsBySegmentId
}) => {
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Box sx={{ 
          width: 32,
          height: 32,
          borderRadius: '50%',
          bgcolor: COLORS.PRIMARY,
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mr: 2,
          fontSize: '16px',
          fontWeight: 500
        }}>
          {activeStep}
        </Box>
        <Typography variant="h6" sx={{ color: COLORS.PRIMARY }}>
          Skills Summary
        </Typography>
      </Box>

      <Box>
        {myReviewer?.skillsMatrixTemplate?.skillsTemplates?.map((template) => (
          <Box 
            key={template.skill.id}
            sx={{ 
              mb: 2,
              p: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="subtitle1" sx={{ color: COLORS.PRIMARY }}>
                {template.skill.name}
              </Typography>
              {template.isRequired === false && (
                <Chip
                  label="Optional"
                  size="small"
                  sx={{
                    height: '20px',
                    fontSize: '10px',
                    backgroundColor: '#FFF3E0',
                    color: '#E65100'
                  }}
                />
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {template.skill.description}
            </Typography>
            <ReviewerCompetencyList
              skillId={template.id}
              segmentId={null}
              competencies={competencies}
              employeeId={employeeId}
              visualizationType={visualizationType}
              isInteractive={false}
              skillName={template.skill.name}
              customDescriptions={currentSkillTemplate?.competencies?.reduce((acc, comp) => {
                acc[comp.competencyLevelId] = comp.description;
                return acc;
              }, {})}
              isLoadingDescriptions={loadingSkillTemplate}
              skillLevels={skillLevels}
              commentsAllowed={true}
              loadingSegmentMap={loadingSegmentMap}
              loadingOverallSkill={loadingOverallSkill}
              assignment={assignment}
              hasMappings={template.mappings?.length > 0}
            />

            {template.mappings?.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
                  Assessed Skills
                </Typography>
                {template.mappings?.map((mapping) => (
                  <Box 
                    key={mapping.id} 
                    sx={{ 
                      mb: 2,
                      p: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      backgroundColor: '#fafafa'
                    }}
                  >
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        {mapping.segment.segment.name}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ 
                          fontStyle: mapping.segment.segment.description ? 'normal' : 'italic'
                        }}
                      >
                        {mapping.segment.segment.description}
                      </Typography>
                    </Box>

                    <ReviewerCompetencyList
                      skillId={template.id}
                      segmentId={mapping.segment.id}
                      competencies={competencies}
                      employeeId={employeeId}
                      visualizationType={visualizationType}
                      isInteractive={false}
                      skillName={mapping.segment.segment.name}
                      customDescriptions={getCustomDescriptionsBySegmentId(mapping.segment.id)}
                      isLoadingDescriptions={loadingSkillTemplate}
                      skillLevels={skillLevels}
                      commentsAllowed={true}
                      loadingSegmentMap={loadingSegmentMap}
                      loadingOverallSkill={loadingOverallSkill}
                      assignment={assignment}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        ))}
      </Box>

      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        mt: 4,
        pt: 3,
        borderTop: '1px solid',
        borderColor: 'divider'
      }}>
        <Box
          onClick={() => activeStep > 1 && !isDirty && handleStep(activeStep - 1, isDirty)()}
          sx={{
            display: 'flex',
            alignItems: 'center',
            color: !isDirty ? COLORS.PRIMARY : 'text.disabled',
            cursor: !isDirty ? 'pointer' : 'not-allowed',
            fontSize: '14px',
            fontWeight: 500,
            '&:hover': {
              opacity: !isDirty ? 0.8 : 1
            }
          }}
        >
          <FaArrowLeft style={{ marginRight: '8px' }} /> Previous
        </Box>
        <Box
          onClick={() => isAllPreviousStepsComplete && !isAssessmentSubmitted && !sessionLocked && !isDirty && !submitting && setConfirmDialogOpen(true)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            color: 'white',
            backgroundColor: COLORS.PRIMARY,
            cursor: isAllPreviousStepsComplete && !isAssessmentSubmitted && !sessionLocked && !isDirty && !submitting ? 'pointer' : 'not-allowed',
            fontSize: '14px',
            fontWeight: 500,
            padding: '8px 16px',
            borderRadius: '4px',
            transition: 'all 0.2s ease',
            opacity: isAllPreviousStepsComplete && !isAssessmentSubmitted && !sessionLocked && !isDirty ? (submitting ? 0.7 : 1) : 0.5,
            '&:hover': {
              opacity: isAllPreviousStepsComplete && !isAssessmentSubmitted && !sessionLocked && !isDirty && !submitting ? 0.9 : (submitting ? 0.7 : 0.5)
            }
          }}
        >
          {sessionLocked
            ? 'Session Closed'
            : isAssessmentSubmitted 
            ? 'Already Submitted' 
            : submitting 
              ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Submitting
                  <CircularProgress 
                    size={16} 
                    thickness={4} 
                    sx={{ color: 'white' }} 
                  />
                </Box>
              ) 
              : 'Submit Assignment'
          }
        </Box>
      </Box>

      <ConfirmationModal
        isOpen={confirmDialogOpen && !isDirty}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={handleSubmitWithConfirmation}
        title="Confirm Submission"
        message="Are you sure you want to submit this skills matrix assignment? This action cannot be undone."
        actionButtonText="Submit"
      />
    </Box>
  );
};

export default SummaryStep; 